/**
 * Hunt form component for create/edit
 */

import { useForm, Controller } from 'react-hook-form';
import { Button, Input, Select } from '../common';
import { DistillerySelect } from '../distilleries';
import { BOTTLE_TYPES } from '../../utils/constants';
import './HuntForm.css';

const PRIORITY_OPTIONS = ['10', '9', '8', '7', '6', '5', '4', '3', '2', '1'];

/**
 * Form for creating or editing a hunt list entry
 * @param {Object} props - Component props
 * @param {Object} props.hunt - Existing hunt data for editing
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.loading - Loading state
 * @returns {React.ReactElement} Form element
 */
export default function HuntForm({ hunt, onSubmit, onCancel, loading = false }) {
  const getDefaultValues = () => {
    if (hunt) {
      return {
        bottle_name: hunt.bottle_name || '',
        type: hunt.type || '',
        priority: hunt.priority != null ? String(hunt.priority) : '5',
        distillery_id: hunt.distillery_id || '',
        notes: hunt.notes || ''
      };
    }
    return {
      bottle_name: '',
      type: '',
      priority: '5',
      distillery_id: '',
      notes: ''
    };
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    defaultValues: getDefaultValues()
  });

  const onFormSubmit = (data) => {
    const processedData = {
      ...data,
      priority: parseInt(data.priority, 10),
      distillery_id: data.distillery_id || null,
      type: data.type || null,
      notes: data.notes || null
    };
    onSubmit(processedData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="hunt-form">
      <div className="hunt-form__grid">
        <Input
          label="Bottle Name"
          required
          error={errors.bottle_name?.message}
          {...register('bottle_name', { required: 'Bottle name is required' })}
        />

        <Select
          label="Type"
          options={['', ...BOTTLE_TYPES]}
          error={errors.type?.message}
          {...register('type')}
        />

        <Select
          label="Priority (10 = Highest)"
          options={PRIORITY_OPTIONS}
          error={errors.priority?.message}
          {...register('priority')}
        />

        <div className="hunt-form__distillery">
          <Controller
            name="distillery_id"
            control={control}
            render={({ field }) => (
              <DistillerySelect
                {...field}
                error={errors.distillery_id?.message}
              />
            )}
          />
        </div>
      </div>

      <div className="hunt-form__notes">
        <div className="input-group">
          <label className="input-group__label">Notes</label>
          <textarea
            className="input-group__input"
            rows="3"
            placeholder="Notes about this hunt..."
            {...register('notes')}
          />
        </div>
      </div>

      <div className="hunt-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {hunt ? 'Save Changes' : 'Add to Hunt List'}
        </Button>
      </div>
    </form>
  );
}
